import { cva } from 'class-variance-authority';
import { type ReactNode, useState } from 'react';

import InfoCircle from '@/assets/svg/bx-info-circle.svg?react';
import { Button, type ButtonProps } from '@/components/buttons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Text } from '@/components/typography';
import { cn } from '@/lib/utils';

type ActionType = 'hover' | 'focus' | 'click' | 'contextMenu';
type InfoPopoverSize = 'medium' | 'large';

type Props = {
  className?: string;
  title: ReactNode;
  body: ReactNode;
  size?: InfoPopoverSize;
  trigger?: ActionType | ActionType[];
  onClick?: ButtonProps['onClick'];
};

/* eslint-disable lingui/no-unlocalized-strings */

const popoverContentVariants = cva('dark p-5', {
  variants: {
    size: {
      medium: 'max-w-60',
      large: 'max-h-80 max-w-80 overflow-y-auto',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

/* eslint-enable lingui/no-unlocalized-strings */

const InfoPopover = ({
  className,
  title,
  body,
  size = 'medium',
  trigger = 'click',
  onClick,
}: Props) => {
  const triggerList = Array.isArray(trigger) ? trigger : [trigger];
  const openOnHover = triggerList.includes('hover');
  const isContextMenuOnlyTrigger = triggerList.includes('contextMenu') && triggerList.length === 1;
  const [open, setOpen] = useState(false);

  return (
    <Popover
      modal={false}
      open={isContextMenuOnlyTrigger ? open : undefined}
      onOpenChange={isContextMenuOnlyTrigger ? setOpen : undefined}
    >
      <PopoverTrigger
        openOnHover={openOnHover}
        render={
          <Button
            variant="ghost"
            className={cn('-my-0.5 text-aqua-400!', className)}
            size="icon-small"
            onClick={onClick}
            onContextMenu={(event) => {
              if (!isContextMenuOnlyTrigger) {
                return;
              }
              event.preventDefault();
              setOpen(true);
            }}
          >
            <InfoCircle />
          </Button>
        }
      />
      <PopoverContent side="right" className={popoverContentVariants({ size })}>
        <div className={cn('flex flex-col gap-2', size === 'large' && 'min-w-0')}>
          <Text variant="caption1" color="light-green">
            {title}
          </Text>
          <Text variant="body2" color="white" className={cn(size === 'large' && 'wrap-break-word')}>
            {body}
          </Text>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InfoPopover;
